<script lang="ts">
export default {
  name: 'Toast',
};
</script>

<script setup lang="ts">
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-vue-next';
import { useToast, type Toast } from '@/composables/useToast';
import { inject, computed } from 'vue';
import { Store } from 'vuex';
import { AppState } from '@/store';

const store = inject<Store<AppState>>('store');
const { toasts, remove } = useToast();

const toastTopStyle = computed(() => {
  const headerHeight = store?.state.resizerRegistry?.['header']?.height ?? 64; // default fallback to typical header height
  const gap = 16;
  return { top: `${headerHeight + gap}px` };
});

const getToastIcon = (type: Toast['type']) => {
  switch (type) {
    case 'success':
      return CheckCircle;
    case 'error':
      return AlertCircle;
    case 'warning':
      return AlertTriangle;
    case 'info':
    default:
      return Info;
  }
};

const getToastClasses = (type: Toast['type']) => {
  const classes = ['tw:alert'];
  
  switch (type) {
    case 'success':
      classes.push('tw:alert-success');
      break;
    case 'error':
      classes.push('tw:alert-error');
      break;
    case 'warning':
      classes.push('tw:alert-warning');
      break;
    case 'info':
    default:
      classes.push('tw:alert-info');
      break;
  }
  
  return classes.join(' ');
};
</script>

<template>
  <div 
    class="tw:fixed tw:right-4 tw:z-[1000] tw:flex tw:flex-col tw:gap-2 tw:max-w-md tw:min-w-80"
    :style="toastTopStyle"
  >
    <div
      v-for="toast in toasts"
      :key="toast.id"
      :class="getToastClasses(toast.type)"
      class="tw:shadow-lg"
    >
      <component :is="getToastIcon(toast.type)" class="tw:w-5 tw:h-5 tw:shrink-0" />
      <div class="tw:flex-1">
        <h3 v-if="toast.title" class="tw:font-bold">{{ toast.title }}</h3>
        <div>{{ toast.message }}</div>
      </div>
      <button
        class="tw:btn tw:btn-sm tw:btn-ghost tw:btn-square"
        @click="remove(toast.id)"
      >
        <X class="tw:w-4 tw:h-4" />
      </button>
    </div>
  </div>
</template>
